import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import {
  Avatar,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
  reset as resetCakeBox,
  setStatus as setCakeBoxStatus,
} from '../../store/cakeBox/cakeBoxSlice';
import { findCakeBox, createCakeBox, patchCakeBox } from '../../store/cakeBox/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import CakeBoxFormModal from './CakeBoxFormModal';
import cakeBoxSchema from '../../validation/cakeBoxSchema';
import { CakeBox } from '../../store/cakeBox/api/interface';
import { getAssetURl } from '../../utils/url';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.cakeBox);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const [search, setSearch] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findCakeBox({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetCakeBox());
  }, [search]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      imgUrl: '',
      price: 0,
      position: '',
      activeState: true,
    },
    validationSchema: cakeBoxSchema,
    onSubmit: async (formValues) => {
      try {
        if (formValues.id) {
          await dispatch(patchCakeBox(formValues)).unwrap();
        }
        if (!formValues.id) {
          const reqBody = omit(formValues, ['id']);
          await dispatch(createCakeBox(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: CakeBox): void => {
    onOpen();
    setValues({
      id: value.id,
      labelEn: value.labelEn || '',
      labelAr: value.labelAr || '',
      imgUrl: value.imgUrl || '',
      price: Number(value.price ?? 0),
      position: String(value.position ?? ''),
      activeState: value?.activeState,
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setCakeBoxStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Page title={'Cake Box: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Cake Box'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={onOpen}
              >
                {'New Cake Box'}
              </Button>
            }
          />

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Image'}</TableCell>
                      <TableCell align='center'>{'English'}</TableCell>
                      <TableCell align='center'>{'Arabic'}</TableCell>
                      <TableCell align='center'>{'Price (QAR)'}</TableCell>
                      <TableCell align='center'>{'Position'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((box) => (
                        <TableRow key={box.id}>
                          <TableCell align='center'>
                            {box.imgUrl ? (
                              <Avatar
                                variant='rounded'
                                src={getAssetURl(box.imgUrl, assetUrl)}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  mx: 'auto',
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  setPreviewImageUrl(
                                    getAssetURl(box.imgUrl || '', assetUrl)
                                  )
                                }
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align='center'>
                            {box?.labelEn || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            {box?.labelAr || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            {Number(box?.price ?? 0).toFixed(2)}
                          </TableCell>
                          <TableCell align='center'>
                            {box?.position}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={box.activeState ? 'success' : 'error'}
                            >
                              {box.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(box)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <CakeBoxFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
          <Dialog
            open={Boolean(previewImageUrl)}
            onClose={() => setPreviewImageUrl('')}
            maxWidth='md'
          >
            <DialogContent>
              {previewImageUrl ? (
                <Box
                  component='img'
                  src={previewImageUrl}
                  alt='Cake box'
                  sx={{ maxWidth: '100%', display: 'block' }}
                />
              ) : null}
            </DialogContent>
          </Dialog>
        </Container>
      </Page>
    </>
  );
};

export default Index;
